import { service } from '@ember/service';
import { cached } from '@glimmer/tracking';

import type { APIData, Service } from 'webapp-types';
import {
	DataPerformanceSummaryRequestBodyObjectType,
	DataPerformanceSummaryType,
} from 'webapp/enums/enums';

import DataPerformanceSummaryBase from 'webapp/models-custom/data-performance-summary/base';

export default class DataPerformanceSummaryGoal extends DataPerformanceSummaryBase {
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;

	objectType = DataPerformanceSummaryType.GOAL;

	@cached
	get object() {
		return this.mmGoalsService.goalModels.find((item) => item.id === this.objectId) || null;
	}

	get goalData() {
		return this.object?.primaryDataModel;
	}

	@cached
	get dateRangeFormula() {
		return this.goalData?.block?.dateRange;
	}

	get title() {
		return this.object?.title || '';
	}

	@cached
	get sources() {
		const singleSource = this.object?.sourceCM;

		return singleSource ? [singleSource] : [];
	}

	get trend() {
		const goalData = this.goalData;

		if (goalData) {
			return goalData.trend;
		}

		return this.defaultTrend;
	}

	override get trendDescription() {
		return this.mmDataPerformanceSummariesService.getGoalTrendDescription(this.trend);
	}

	get isDataIncomplete() {
		return this.goalData?.isDataIncomplete;
	}

	get isDataEmpty() {
		return !this.goalData?.hasValue;
	}

	get trackingObjectType() {
		return 'goal';
	}

	get trackingMetricsNames() {
		return [this.title];
	}

	get summariesRequestBodyBase(): APIData.GenerativeAI.DataPerformance.SummariesRequest | null {
		if (this.object?.blockId) {
			return {
				typeObject: DataPerformanceSummaryRequestBodyObjectType.BLOCK,
				typeObjectId: this.object.blockId,
			};
		}

		return null;
	}

	@cached
	get metricSetupData() {
		if (this.object) {
			return this.object.mainMetricSetup.metricSetupData;
		}

		return null;
	}

	@cached
	get singleOutputMetricSetupData() {
		return this.metricSetupData; // Goals always have a single attribute and can't have Unit.ALL so we don't test isSingleOutput
	}
}
